// @flow
import * as React from 'react';
import { List, Map } from 'immutable';

import { TourSummaries } from 'common/models/internal/PlanSummary';

import { MarkerTypeEnum, MarkerType } from 'common/enums/MapEnum';

import { type TaskEditEvent } from 'common/models/flow/PlanEditsV2';
import { type ColorType } from 'common/utils/TourColorDecider';
import { type TourVisitIdentifierType } from 'common/contexts/VisitStateContext';
import BeatVisitMarker from './BeatVisitMarker';
import type { UnallocatedVisitType } from './FpAllocatedVisitsContainer';

import { neoUIColors } from 'common/enums/ColorEnum';
import { type FpBeat } from 'common/models/internal/FpBeat';
import { type VisitIdOutletType } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';

type ContextMenuType = {
    x: number,
    y: number,
    tourId: number,
    eventId: number
};

export type Props = {
    markerType: $Values<typeof MarkerTypeEnum>,
    selectedVisits: List<TourVisitIdentifierType>,
    tourIdToEditsMap?: Map<string, List<Object>>,
    visitsBeingEdited?: List<TaskEditEvent>,
    getVisitLoadingColor?: (
        taskBeingEdited: TaskEditEvent,
        tourColors: Map<string, ColorType>
    ) => string,
    isPlanFrozenForPersonnel?: boolean,
    showMarkers: boolean,
    contextMenu: ContextMenuType,
    disableFurtherEdits: boolean,
    isLegacyPlan: boolean,
    hasFailedEdits?: boolean,
    tourSummaries?: Map<string, TourSummaries>,
    setActiveVisit: (tourVisitIdentifier: ?TourVisitIdentifierType) => void,
    setHoveredVisit: (tourVisitIdentifier: ?TourVisitIdentifierType) => void,
    onContextMenu: (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => void,
    parentElRefForMarker: {
        current: ?HTMLDivElement
    },
    isGlobalPersonnel: boolean,
    planId: string,
    onHover: (beatId?: string, visitId: string) => void,
    onHoverOut: () => void,
    onPopUpCloseClick: () => void,
    unallocatedVisits: List<UnallocatedVisitType>,
    selectedUnallocatedVisit: ?string,
    hoveredUnallocatedVisit: ?string,
    selectedBeat: ?string,
    visibleBeats: List<FpBeat>,
    visitIdMap: Map<string, VisitIdOutletType>,
    beatSelectionMap: Map<string, boolean>,
    selectedVisit: ?Object,
    hoveredBeat: string,
    hoveredVisit: string
};

export default class FpBeatMarkers {
    static getUnallocatedMarkers(props: Props) {
        const {
            unallocatedVisits,
            selectedUnallocatedVisit,
            hoveredUnallocatedVisit,
            ...rest
        } = props;
        const visitMarkers = unallocatedVisits.map((visit, index) => {
            const { id } = visit;
            const key = id;
            const visitLatLng = visit.address.latLng;
            const { lat, lng } = visitLatLng;
            const color = neoUIColors.red400;
            const isSelected = selectedUnallocatedVisit === visit.id;
            const isHovered = hoveredUnallocatedVisit === visit.id;
            const isPopupOpen = isSelected || isHovered;

            return (
                <BeatVisitMarker
                    {...rest}
                    key={key}
                    outletData={visit}
                    hoveredVisit={hoveredUnallocatedVisit}
                    withArrow
                    markerType={MarkerType.HOVERABLE}
                    salesCategory={visit.category}
                    salesmanId={null}
                    beatId={null}
                    visit={visit}
                    id={index}
                    lat={lat}
                    lng={lng}
                    isSelected={isSelected}
                    selectedUnallocatedVisit={selectedUnallocatedVisit}
                    hoveredUnallocatedVisit={hoveredUnallocatedVisit}
                    isPopupOpen={isPopupOpen}
                    isHovered={isHovered}
                    label={index + 1}
                    color={color}
                    isUnallocated
                    isContextMenuOpen={false}
                    sourceIndex={index}
                    visitIndex={0}
                />
            );
        });
        return visitMarkers;
    }

    static getHomebaseMarkers(props: Props) {
        let homebaseMarkers = [];
        props.visibleBeats.forEach(beat => {
            const isBeatSelected = props.beatSelectionMap.get(beat.beatId);
            const { visits } = beat;
            const homebaseColor = neoUIColors.grey500;
            if (isBeatSelected) {
                const homebaseVisits = visits.filter(visit => {
                    return visit.userVisitType === 'RELOAD';
                });
                const allHomebaseMarkers = homebaseVisits.map(
                    (homebase, index) => {
                        const key = `${beat.beatId}-homebase`;

                        const { visitLatLng } = homebase;

                        const isHomebaseHovered =
                            beat.beatId === props.hoveredBeat &&
                            homebase.id === props.hoveredVisit;

                        const isHomebaseSelected =
                            beat.beatId === props.selectedBeat &&
                            homebase.id === props.selectedVisit;
                        return (
                            <BeatVisitMarker
                                {...props}
                                key={key}
                                id={index}
                                withArrow
                                markerType={MarkerType.HOVERABLE}
                                salesCategory={beat.categories[0]}
                                salesmanId={beat.currentSalesmanId}
                                beatId={beat.beatId}
                                visit={homebase}
                                lat={visitLatLng.lat}
                                lng={visitLatLng.lng}
                                isPopupOpen={
                                    isHomebaseHovered || isHomebaseSelected
                                }
                                isSelected={isHomebaseSelected}
                                isHovered={isHomebaseHovered}
                                label={props.t('entities:homebase')}
                                icon="home"
                                isHomebaseMarker
                                color={homebaseColor}
                                onMarkerPopUpCloseClick={
                                    props.onPopUpCloseClick
                                }
                                isPlanEditable
                                isContextMenuOpen={false}
                                sourceIndex={index}
                                visitIndex={index}
                            />
                        );
                    }
                );
                homebaseMarkers = [...homebaseMarkers, ...allHomebaseMarkers];
            }
        });
        return homebaseMarkers;
    }

    static getFlagMarkers(props: Props) {
        const beatFlagMarkers = [];
        props.visibleBeats.forEach(beat => {
            const isBeatSelected = props.beatSelectionMap.get(beat.beatId);
            const { visits } = beat;
            if (isBeatSelected) {
                const [startVisit] = visits;
                const endVisit = visits[visits.length - 1];
                const FlagColor = neoUIColors.grey500;
                if (!startVisit.outletVisitId) {
                    const [startVisit] = visits;
                    const startVisitLatLng = startVisit.visitLatLng;

                    const isStartFlagHovered =
                        beat.beatId === props.hoveredBeat &&
                        startVisit.id === props.hoveredVisit;

                    const isStartFlagSelected =
                        beat.beatId === props.selectedBeat &&
                        startVisit.id === props.selectedVisit;

                    const startVisitMarker = (
                        <BeatVisitMarker
                            {...props}
                            id={0}
                            key={`${beat.beatId}-start`}
                            withArrow
                            markerType={MarkerType.HOVERABLE}
                            salesCategory={beat.categories[0]}
                            salesmanId={beat.currentSalesmanId}
                            beatId={beat.beatId}
                            visit={startVisit}
                            isSelected={isStartFlagSelected}
                            isHovered={isStartFlagHovered}
                            isPopupOpen={
                                isStartFlagSelected || isStartFlagHovered
                            }
                            lat={startVisitLatLng.lat}
                            lng={startVisitLatLng.lng}
                            icon="flagMarker"
                            label={props.t(
                                'entityManagement:fpPlanView.shiftStart'
                            )}
                            color={FlagColor}
                            onMarkerPopUpCloseClick={props.onPopUpCloseClick}
                            isFlagMarker
                            isContextMenuOpen={false}
                            sourceIndex={0}
                            visitIndex={0}
                        />
                    );
                    beatFlagMarkers.push(startVisitMarker);
                }
                if (!endVisit.outletVisitId) {
                    const endVisitLatLng = endVisit.visitLatLng;
                    const isEndFlagHovered =
                        beat.beatId === props.hoveredBeat &&
                        endVisit.id === props.hoveredVisit;
                    const isEndFlagSelected =
                        beat.beatId === props.selectedBeat &&
                        endVisit.id === props.selectedVisit;
                    const endVisitMarker = (
                        <BeatVisitMarker
                            {...props}
                            key={`${beat.beatId}-end`}
                            withArrow
                            id={visits.length - 1}
                            icon="truck_complete"
                            markerType={MarkerType.HOVERABLE}
                            salesCategory={beat.categories[0]}
                            salesmanId={beat.currentSalesmanId}
                            beatId={beat.beatId}
                            isSelected={isEndFlagSelected}
                            isHovered={isEndFlagHovered}
                            isPopupUp={isEndFlagSelected || isEndFlagHovered}
                            visit={endVisit}
                            label={props.t(
                                'entityManagement:fpPlanView.shiftEnd'
                            )}
                            lat={endVisitLatLng.lat}
                            lng={endVisitLatLng.lng}
                            color={FlagColor}
                            onMarkerPopUpCloseClick={props.onPopUpCloseClick}
                            isFlagMarker
                            isContextMenuOpen={false}
                            sourceIndex={visits.size}
                        />
                    );
                    beatFlagMarkers.push(endVisitMarker);
                }
            }
        });
        return beatFlagMarkers;
    }

    static getMarkers(props: Props) {
        const {
            selectedBeat,
            visitIdMap,
            visibleBeats,
            beatSelectionMap,
            selectedVisit,
            hoveredBeat,
            hoveredVisit,
            onPopUpCloseClick,
            ...rest
        } = props;

        let beatVisitMarkers = [];
        visibleBeats.forEach(beat => {
            const { visits } = beat;
            const isBeatSelected = beatSelectionMap.get(beat.beatId);
            if (!isBeatSelected) {
                return null;
            } else {
                const filteredVisits = visits.filter(visit => {
                    return visit.outletVisitId;
                });
                const allVisitMarkers = filteredVisits.map((visit, index) => {
                    const { outletVisitId } = visit;

                    const outletData = outletVisitId
                        ? visitIdMap.get(outletVisitId)
                        : null;
                    const key = `${beat.beatId}${visit.id}`;

                    const visitLatLng = outletVisitId
                        ? outletData.address.latLng
                        : visit.visitLatLng;

                    const { lat, lng } = visitLatLng;

                    const color = neoUIColors.grey500;

                    const isHovered =
                        beat.beatId === hoveredBeat &&
                        outletVisitId === hoveredVisit;

                    const isSelected =
                        beat.beatId === selectedBeat &&
                        outletVisitId === selectedVisit;
                    return (
                        <BeatVisitMarker
                            {...rest}
                            key={key}
                            id={index}
                            outletData={outletData}
                            withArrow
                            hoveredVisit={hoveredVisit}
                            markerType={MarkerType.HOVERABLE}
                            salesCategory={beat.categories[0]}
                            salesmanId={beat.currentSalesmanId}
                            beatId={beat.beatId}
                            visit={visit}
                            lat={lat}
                            lng={lng}
                            isPopupOpen={isHovered || isSelected}
                            isSelected={isSelected}
                            isHovered={isHovered}
                            label={index + 1}
                            color={color}
                            onMarkerPopUpCloseClick={onPopUpCloseClick}
                            isPlanEditable
                            isContextMenuOpen={false}
                            sourceIndex={index}
                            visitIndex={index}
                        />
                    );
                });
                beatVisitMarkers = [...beatVisitMarkers, ...allVisitMarkers];
            }
        });

        return beatVisitMarkers;
    }
}
